/**
 * Streaming client for the Google ADK agent server used by ChatWidget.
 *
 * Ported from dingoo-dashboards/src/utils/adkChat.ts. Trimmed to a single
 * `streamAdkMessage()` async iterator that yields the assistant's accumulated
 * text as SSE chunks arrive.
 *
 * ADK requires the session to be created (POST /apps/{app}/users/{userId}
 * /sessions/{sessionId}) before /run_sse will accept messages. We cache the
 * (userId, sessionId) pairs we've already created so the second turn skips
 * the round-trip.
 */
import { auth } from "firebase/firebase"

export const AGENT_URL = process.env.REACT_APP_AGENT_URL ?? ""
const APP_NAME = "agent_ops"

const createdSessions = new Set<string>()

async function getAuthHeaders(): Promise<Record<string, string>> {
	const headers: Record<string, string> = {
		"Content-Type": "application/json",
	}
	const token = await auth.currentUser?.getIdToken()
	if (token) headers["Authorization"] = `Bearer ${token}`
	return headers
}

async function ensureSession(userId: string, sessionId: string): Promise<void> {
	const key = `${userId}:${sessionId}`
	if (createdSessions.has(key)) return

	const headers = await getAuthHeaders()
	const res = await fetch(
		`${AGENT_URL}/apps/${APP_NAME}/users/${userId}/sessions/${sessionId}`,
		{ method: "POST", headers, body: JSON.stringify({}) }
	)
	if (res.ok || res.status === 409) {
		createdSessions.add(key)
		return
	}

	// Fallback: some ADK builds want POST without the id in the path.
	const res2 = await fetch(
		`${AGENT_URL}/apps/${APP_NAME}/users/${userId}/sessions`,
		{ method: "POST", headers, body: JSON.stringify({ id: sessionId }) }
	)
	if (!res2.ok) {
		throw new Error(`Failed to create ADK session: ${res2.status}`)
	}
	createdSessions.add(key)
}

interface StreamArgs {
	userId: string
	sessionId: string
	message: string
	signal?: AbortSignal
}

/**
 * Stream a single user turn through ADK and yield the assistant's running
 * text after each SSE chunk. The yielded string is the *accumulated* reply
 * so callers can replace the rendered bubble each time.
 */
export async function* streamAdkMessage({
	userId,
	sessionId,
	message,
	signal,
}: StreamArgs): AsyncGenerator<string, void, void> {
	if (!AGENT_URL) {
		throw new Error(
			"REACT_APP_AGENT_URL is not set — configure it in your .env file."
		)
	}

	await ensureSession(userId, sessionId)

	const headers = await getAuthHeaders()
	const init: RequestInit = {
		method: "POST",
		headers,
		body: JSON.stringify({
			app_name: APP_NAME,
			user_id: userId,
			session_id: sessionId,
			new_message: { role: "user", parts: [{ text: message }] },
			streaming: true,
		}),
	}
	if (signal) init.signal = signal
	const response = await fetch(`${AGENT_URL}/run_sse`, init)

	if (!response.ok) {
		const errText = await response.text().catch(() => "")
		throw new Error(`ADK request failed: ${response.status} ${errText}`)
	}

	const reader = response.body?.getReader()
	if (!reader) throw new Error("ADK response had no body")

	const decoder = new TextDecoder()
	let accumulated = ""
	let buffer = ""

	try {
		while (true) {
			const { done, value } = await reader.read()
			if (done) break

			buffer += decoder.decode(value, { stream: true })
			const lines = buffer.split("\n")
			buffer = lines.pop() ?? ""

			for (const line of lines) {
				if (!line.startsWith("data: ")) continue
				const data = line.slice(6).trim()
				if (data === "[DONE]") continue

				try {
					const event = JSON.parse(data) as Record<string, unknown>
					// The final non-partial event duplicates the streamed text; skip it.
					if (event["partial"] === false) continue
					const chunk = extractTextFromEvent(event)
					if (chunk) {
						accumulated += chunk
						yield accumulated
					}
				} catch {
					// Ignore malformed SSE payloads.
				}
			}
		}
	} finally {
		reader.releaseLock()
	}
}

function extractTextFromEvent(event: Record<string, unknown>): string | null {
	const content = event["content"] as Record<string, unknown> | undefined
	if (!content) return null
	const parts = content["parts"] as Array<Record<string, unknown>> | undefined
	if (!Array.isArray(parts)) return null

	const texts = parts
		.filter(
			(p) =>
				typeof p["text"] === "string" &&
				!p["functionCall"] &&
				!p["functionResponse"]
		)
		.map((p) => p["text"] as string)

	return texts.length > 0 ? texts.join("") : null
}

export function generateSessionId(): string {
	return `session_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`
}
